import { useQuery } from '@tanstack/react-query'
import { PlusCircle, Vault as VaultIcon } from 'lucide-react'
import { useState } from 'react'

import { tillListQueryOptions, vaultsQueryOptions } from '@/api/teller-api'
import { ErrorSurface } from '@/components/error-surface'
import { MoneyDisplay } from '@/components/money-display'
import { PageHeader } from '@/components/page-header'
import { RequiresWiderScreen } from '@/components/requires-wider-screen'
import { StatusBadge } from '@/components/status-badge'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { tillTypeLabel } from '@/features/teller/constants'
import { TELLER_PERMISSIONS, useTellerPermissions } from '@/features/teller/permissions'
import { CreateTillDialog, CreateVaultDialog } from '@/features/teller/setup-actions'
import { Section, VaultPanel } from '@/features/teller/teller-panels'
import { usePostingDay } from '@/features/teller/use-posting-day'

/**
 * Branch infrastructure: the vault a branch keeps its cash in, and the drawers that are floated
 * from it. Built once when a counter is built, not operated.
 *
 * There is a real and awkward mismatch on this screen, and it is stated rather than designed
 * around. Creating either needs `admin.branch.manage`, which is held by SystemAdmin and
 * SuperAdministrator — and the SystemAdmin role is declared as forbidding financial permissions, so
 * it holds neither `teller.till.view` nor `teller.vault.view`. The person who can build a till
 * usually cannot read the list of tills. So the existing-infrastructure panels render only when the
 * reading permission is actually held, and when it is not the screen says plainly that a duplicate
 * till number or vault name will be discovered on submission rather than beforehand — which is
 * survivable, because both duplicate checks run before the approval gate and answer immediately.
 */
export function TellerSetupPage() {
  const permissions = useTellerPermissions()
  const day = usePostingDay()

  const [creatingTill, setCreatingTill] = useState(false)
  const [creatingVault, setCreatingVault] = useState(false)

  const vaults = useQuery({
    ...vaultsQueryOptions(day.branchId),
    enabled: permissions.canViewVaults && Boolean(day.branchId),
  })

  const tills = useQuery({
    ...tillListQueryOptions({ branchId: day.branchId ?? undefined, page: 1, pageSize: 100 }),
    enabled: permissions.canViewTills && Boolean(day.branchId),
  })

  const canRead = permissions.canViewTills || permissions.canViewVaults

  return (
    <RequiresWiderScreen
      screen="Till and vault setup"
      reason="A cash holding limit is a keyed amount, and a till is permanent branch infrastructure — it deserves a considered desk rather than a bus."
    >
      <PageHeader
        title="Tills and vaults"
        description="Branch infrastructure. A vault holds the branch's cash; every drawer is floated out of it and banked back into it."
        actions={
          permissions.canManageInfrastructure ? (
            <>
              <Button variant="secondary" onClick={() => setCreatingVault(true)} disabled={!day.branchId}>
                <VaultIcon />
                New vault
              </Button>
              <Button variant="primary" onClick={() => setCreatingTill(true)} disabled={!day.branchId}>
                <PlusCircle />
                New till
              </Button>
            </>
          ) : null
        }
      />

      {!permissions.canManageInfrastructure ? (
        <Alert tone="warning">
          <div className="space-y-1">
            <AlertTitle>Creating a till or a vault is not open to your role</AlertTitle>
            <AlertDescription>
              Both need <span className="font-mono text-xs">{TELLER_PERMISSIONS.infrastructureManage}</span>{' '}
              — an <span className="font-mono text-xs">admin.*</span> code by design, because a till
              is branch infrastructure rather than something a teller operates. What is listed below
              is read-only for you.
            </AlertDescription>
          </div>
        </Alert>
      ) : null}

      {!canRead ? (
        <Alert tone="warning">
          <div className="space-y-1">
            <AlertTitle>You cannot see what already exists here</AlertTitle>
            <AlertDescription>
              Listing drawers needs <span className="font-mono text-xs">{TELLER_PERMISSIONS.tillView}</span>{' '}
              and vaults need <span className="font-mono text-xs">{TELLER_PERMISSIONS.vaultView}</span>,
              neither of which an administrative role holds — the SystemAdmin role is declared as
              forbidding financial permissions. So a duplicate till number (
              <span className="font-mono text-xs">teller.till_number_taken</span>) or vault name (
              <span className="font-mono text-xs">teller.vault_already_exists</span>) is discovered
              when you submit, not before. Both are checked ahead of the approval gate, so the answer
              comes back at once rather than at countersignature.
            </AlertDescription>
          </div>
        </Alert>
      ) : null}

      {permissions.canViewVaults ? (
        vaults.isPending ? (
          <Skeleton className="h-32 w-full" />
        ) : vaults.isError ? (
          <ErrorSurface
            error={vaults.error}
            onRetry={() => void vaults.refetch()}
            title="The vaults could not be read"
          />
        ) : (
          <VaultPanel vaults={vaults.data} />
        )
      ) : null}

      {permissions.canViewTills ? (
        tills.isPending ? (
          <Skeleton className="h-48 w-full" />
        ) : tills.isError ? (
          <ErrorSurface
            error={tills.error}
            onRetry={() => void tills.refetch()}
            title="The tills could not be read"
          />
        ) : (
          <Section
            title="Tills"
            description="Counter drawers are worked in shifts; field drawers are assigned to an officer for a day."
          >
            {tills.data.items.length === 0 ? (
              <p className="text-sm text-muted">
                This branch has no till. Until one exists, no counter can be opened and no cash can
                be taken over one.
              </p>
            ) : (
              <ul className="divide-y divide-border-subtle">
                {tills.data.items.map((till) => (
                  <li
                    key={till.id}
                    className="flex flex-wrap items-center justify-between gap-3 py-2.5 first:pt-0"
                  >
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-heading">
                        <span className="font-mono text-xs">{till.tillNumber}</span> · {till.name}
                      </p>
                      <p className="text-2xs text-muted">
                        {tillTypeLabel(till.tillType)} · {till.glAccountCode}
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      <StatusBadge status={till.status} />
                      <MoneyDisplay value={till.cashOnHandMinor} currency={till.currencyCode} />
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Section>
        )
      ) : null}

      {day.branchId ? (
        <>
          <CreateTillDialog branchId={day.branchId} open={creatingTill} onOpenChange={setCreatingTill} />
          <CreateVaultDialog branchId={day.branchId} open={creatingVault} onOpenChange={setCreatingVault} />
        </>
      ) : null}
    </RequiresWiderScreen>
  )
}
